import request from '@/utils/request'

// 响应拦截器已在 request.js 中统一处理

export class ApiService {
    // 获取文件列表
    static async getFileList() {
        try {
            console.log('获取文件列表...')
            const response = await request.get('/files/list')
            console.log('文件列表:', response)
            return response
        } catch (error) {
            console.error('获取文件列表失败:', error)
            throw error
        }
    }

    // 获取文件内容
    static async getFileContent(path) {
        try {
            console.log('获取文件内容, 路径:', path)
            const response = await request.get('/files/content', {
                params: { path }
            })
            console.log('获取到的文件内容:', response)
            return response
        } catch (error) {
            console.error('获取文件内容失败:', error)
            throw error
        }
    }

    // 获取完整的JSON文档对象（用于精确渲染和批注）
    static async getDocument(path) {
        try {
            console.log('获取文档对象, 路径:', path)
            const response = await request.get('/files/document', {
                params: { path }
            })
            console.log('获取到的文档对象:', response)
            return response
        } catch (error) {
            console.error('获取文档对象失败:', error)
            throw error
        }
    }

    // 保存文件
    static async saveFile(fileId, content) {
        return request.post(`/files/${fileId}`, { content })
    }

    // 删除文件
    static async deleteFile(path) {
        try {
            console.log('删除文件:', path)
            const response = await request.delete('/files/file', { params: { path } })
            return response
        } catch (error) {
            console.error('删除文件失败:', error)
            throw error
        }
    }

    // 机器翻译
    static async translate(text) {
        try {
            console.log('开始翻译:', text)
            const response = await request.post('/translate', { text })
            
            // 处理API返回格式变化
            // 如果API直接返回了翻译内容（字符串），转换为标准格式
            if (typeof response === 'string') {
                return {
                    success: true,
                    content: response,
                    message: '翻译成功'
                }
            }
            
            // 如果API返回的是一个仅包含translatedText字段的对象
            if (response.translatedText) {
                return {
                    success: true,
                    content: response.translatedText,
                    message: '翻译成功'
                }
            }
            
            return response
        } catch (error) {
            console.error('翻译失败:', error)
            // 返回一个包含错误信息的对象，避免抛出异常
            return {
                success: false,
                content: '',
                message: error.message || '翻译失败'
            }
        }
    }

    // 获取翻译评分
    static async getScore(sourceText, translatedText) {
        try {
            console.log('开始评分:', { sourceText, translatedText })
            const response = await request.post('/translate/score', {
                sourceText,
                translatedText
            })
            
            // 处理API返回格式变化
            // 如果API直接返回了评分内容（字符串），转换为标准格式
            if (typeof response === 'string') {
                return {
                    success: true,
                    content: response,
                    message: '评分成功',
                    score: ApiService.extractScoreFromText(response)
                }
            }
            
            // 如果API返回的是一个仅包含文本内容的对象
            if (response.translatedText) {
                return {
                    success: true,
                    content: response.translatedText,
                    message: '评分成功',
                    score: ApiService.extractScoreFromText(response.translatedText)
                }
            }
            
            return response
        } catch (error) {
            console.error('评分失败:', error)
            return {
                success: false,
                content: `评分失败：${error.message || '未知错误'}`,
                message: error.message || '评分失败'
            }
        }
    }
    
    // 辅助函数：从文本中提取分数
    static extractScoreFromText(text) {
        try {
            // 尝试从文本中提取分数 (例如: "得分：42/50分")
            const scoreRegex = /(\d+)\/(\d+)分/;
            const match = text.match(scoreRegex);
            if (match && match[1]) {
                return parseInt(match[1], 10);
            }
            return null;
        } catch (error) {
            console.error('从文本提取分数失败:', error);
            return null;
        }
    }

    // 删除评分记录
    static async deleteScore(scoreId) {
        try {
            console.log('通过服务端删除评分记录:', scoreId)
            // 如果后端有提供删除API，则调用
            // const response = await request.delete(`/scores/${scoreId}`)
            // return response
            
            // 如果没有后端API，直接调用Supabase但带上服务角色token
            // 注意：这仅是临时解决方案，最好的做法是在后端实现一个删除API
            
            // 抛出错误以便调用方可以回退到前端删除逻辑
            throw new Error('服务端删除API尚未实现')
        } catch (error) {
            console.error('服务端删除评分记录失败:', error)
            throw error
        }
    }

    // 提取术语
    static async getTerminology(text) {
        try {
            console.log('提取术语:', text)
            const response = await request.post('/translate/terminology', { text })
            
            // 检查response是否包含术语分类数据
            // 如果是直接返回术语数据的对象，通常会有多个分类键值对
            if (response && typeof response === 'object' && !response.data) {
                const hasTerms = Object.keys(response).some(key => {
                    return response[key] && typeof response[key] === 'string' && response[key].trim() !== '';
                });
                
                if (hasTerms) {
                    // 如果直接返回术语分类对象，则包装为标准格式
                    return {
                        success: true,
                        data: response,
                        message: '术语提取成功'
                    };
                }
            }
            
            // 处理API返回格式的变化 - 适配不同的返回格式
            // 如果API直接返回了错误消息（字符串），将其转换为正确的对象格式
            if (typeof response === 'string') {
                // 检查是否包含"未找到任何专业术语"的提示
                if (response.includes('未找到任何专业术语')) {
                    // 返回空数据但标记为成功
                    return {
                        success: true,
                        data: {},
                        message: '未在文本中找到任何专业术语'
                    }
                }
                
                // 其他错误消息，返回错误但避免抛出异常
                return {
                    success: true,
                    data: {},
                    message: response
                }
            }
            
            // 处理保存结果的情况 {path: 'xxx', contentLength: xxx}
            if (response && response.path && response.contentLength !== undefined) {
                console.log('术语已保存到文件:', response);
                // 返回一个成功标记，告知前端数据已经保存，但没有术语数据
                return {
                    success: true,
                    message: '术语已保存到文件',
                    path: response.path,
                    contentLength: response.contentLength
                }
            }
            
            return response
        } catch (error) {
            console.error('术语检查失败:', error)
            // 即使出错也返回一个有效对象而非抛出异常
            // 400或404错误通常表示没有找到术语，我们将其视为正常情况
            if (error.response && (error.response.status === 400 || error.response.status === 404)) {
                // 尝试从错误响应中获取消息
                let message = '未在文本中找到任何专业术语';
                if (error.response.data && typeof error.response.data === 'string') {
                    message = error.response.data;
                } else if (error.response.data && error.response.data.message) {
                    message = error.response.data.message;
                }
                
                return {
                    success: true,
                    data: {},
                    message: message
                }
            }
            
            // 其他类型的错误，仍然返回对象而不是抛出异常
            return {
                success: false,
                data: {},
                message: error.message || '术语提取失败'
            }
        }
    }

    // 搜索文件内容
    static async search(query) {
        try {
            console.log('开始搜索:', query)
            // 使用请求拦截器中已配置的Authorization头
            const response = await request.post('/files/search', { query })
            return response
        } catch (error) {
            console.error('搜索失败:', error)
            throw error
        }
    }

    // 获取文本解析
    static async analyze(text) {
        try {
            console.log('开始解析文本:', text)
            const response = await request.post('/translate/analysis', { text })
            
            // 处理API返回格式变化
            // 如果API直接返回了解析内容（字符串），转换为标准格式
            if (typeof response === 'string') {
                return {
                    success: true,
                    content: response,
                    message: '解析成功'
                }
            }
            
            // 如果API返回的是一个仅包含文本内容的对象
            if (response.translatedText) {
                return {
                    success: true,
                    content: response.translatedText,
                    message: '解析成功'
                }
            }
            
            return response
        } catch (error) {
            console.error('解析失败:', error)
            // 检查是否是404错误
            if (error.response && error.response.status === 404) {
                // 对于404错误，可能是API端点变更或服务暂不可用
                return {
                    success: false,
                    content: `解析服务暂不可用，请稍后再试。`,
                    message: '解析服务暂不可用'
                }
            }
            
            // 尝试从错误响应中获取更详细的信息
            let errorMessage = error.message || '未知错误';
            let errorContent = `解析失败：${errorMessage}`;
            
            if (error.response && error.response.data) {
                if (typeof error.response.data === 'string') {
                    errorContent = error.response.data;
                    errorMessage = error.response.data;
                } else if (error.response.data.message) {
                    errorContent = error.response.data.message;
                    errorMessage = error.response.data.message;
                }
            }
            
            // 返回一个包含错误信息的对象，避免抛出异常
            return {
                success: false,
                content: errorContent,
                message: errorMessage
            }
        }
    }

    // 保存译文
    static async saveTranslation(path, content) {
        try {
            console.log('保存译文:', { path, contentLength: content.length })
            const response = await request.post('/files/translation', { path, content })
            return response
        } catch (error) {
            console.error('保存译文失败:', error)
            throw error
        }
    }

    // 获取译文
    static async getTranslation(path) {
        try {
            console.log('获取译文:', path)
            // 确保路径被正确编码
            const encodedPath = encodeURIComponent(path)
            const response = await request.get('/files/translation', {
                params: { path: encodedPath }
            })
            return response
        } catch (error) {
            console.error('获取译文失败:', error)
            throw error
        }
    }
    static async deleteTranslation(path) {
        try {
            console.log('删除译文文件:', path)
            const response = await request.delete('/files/translation', {
                params: { path }
            })
            return response
        } catch (error) {
            console.error('删除译文文件失败:', error)
            throw error
        }
    }

    // 获取翻译状态
    static async getTranslationStatus() {
        try {
            const response = await request.get('/translate/status')
            return response
        } catch (error) {
            console.error('获取翻译状态失败:', error)
            return null
        }
    }

    // 获取所有任务的状态
    static async getTaskStatus() {
        try {
            const response = await request.get('/translate/task-status')
            return response
        } catch (error) {
            console.error('获取任务状态失败:', error)
            return {}
        }
    }

    // 创建文件夹
    static async createFolder(folderName) {
        try {
            console.log('创建文件夹:', folderName)
            const response = await request.post('/files/folder', { folderName })
            return response
        } catch (error) {
            console.error('创建文件夹失败:', error)
            throw error
        }
    }

    // 重命名文件
    static async renameFile(oldPath, newPath) {
        try {
            console.log('重命名文件:', oldPath, newPath)
            const response = await request.post('/files/rename', { oldPath, newPath })
            return response
        } catch (error) {
            console.error('重命名文件失败:', error)
            throw error
        }
    }

    // 重命名文件夹
    static async renameFolder(oldFolder, newFolder) {
        try {
            console.log('重命名文件夹:', oldFolder, newFolder)
            const response = await request.post('/files/rename-folder', { oldFolder, newFolder })
            return response
        } catch (error) {
            console.error('重命名文件夹失败:', error)
            throw error
        }
    }

    // 删除文件夹
    static async deleteFolder(folder) {
        try {
            console.log('删除文件夹:', folder)
            const response = await request.delete('/files/folder', { params: { folder } })
            return response
        } catch (error) {
            console.error('删除文件夹失败:', error)
            throw error
        }
    }

    // 验证验证码

    // 自动处理（同时翻译、术语检查和解析）
    static async autoProcess(text) {
        try {
            console.log('开始自动处理文本:', text)
            const response = await request.post('/translate/auto', { text })
            return response
        } catch (error) {
            console.error('自动处理失败:', error)
            throw error
        }
    }

    // 获取文件的批注列表
    static async getAnnotationsByFile(filePath) {
        try {
            console.log('获取文件批注列表, 文件路径:', filePath)
            const response = await request.get('/annotations/file', {
                params: { filePath: encodeURIComponent(filePath) }
            })
            console.log('获取到的文件批注列表:', response)
            return response
        } catch (error) {
            console.error('获取文件批注失败:', error)
            throw error
        }
    }

    // 获取文件指定段落的批注列表
    static async getAnnotationsByFileAndPassage(filePath, passageIndex) {
        try {
            console.log('获取段落批注列表, 文件路径:', filePath, '段落索引:', passageIndex)
            const response = await request.get('/annotations/file/passage', {
                params: { filePath: encodeURIComponent(filePath), passageIndex: encodeURIComponent(passageIndex) }
            })
            console.log('获取到的段落批注列表:', response)
            return response
        } catch (error) {
            console.error('获取段落批注失败:', error)
            throw error
        }
    }

    // 获取指定位置的批注
    static async getAnnotationsByPosition(filePath, passageIndex, position) {
        try {
            console.log('获取位置批注, 文件路径:', filePath, '段落索引:', passageIndex, '位置:', position)
            const response = await request.get('/annotations/file/passage/position', {
                params: { filePath: encodeURIComponent(filePath), passageIndex: encodeURIComponent(passageIndex), position: position }
            })
            console.log('获取到的位置批注:', response)
            return response
        } catch (error) {
            console.error('获取位置批注失败:', error)
            throw error
        }
    }
}